import React from 'react';
import { Router } from 'react-router-dom';
import moment from 'moment';
import { createRoot } from 'react-dom/client';

import appConfig from 'app-config';
import 'console-config';
import HomeClient from 'flow/api-clients/home-client';
import getRoutes from './routes';

import { clearState, clearLoginData } from 'flow/actions/app-actions';
import { disconnectApp } from 'flow/actions/navigate-actions';
import { i18next } from 'flow/configure-locale';
import { history, store } from 'flow/configure-store';

import {
    addLanguageTohtml,
    getApplicationLanguage,
    setLanguageDirection,
} from 'flow/utils/browser-utils';
import { electronListeners } from 'flow/utils/electron-utils';
import { isUnauthorizedSafePath } from 'flow/utils/router-utils';
import ErrorBoundary from 'flow/components/error-boundary/ErrorBoundary';
import isElectron from 'is-electron';

import {
    isTeamsPopupUrl,
    TeamsAuthenticationPopup,
} from 'flow/route-components/TeamsAuthentication';
import MeetingMainUnauthorized from 'flow/components/meeting/shared/MeetingMainUnauthorized';

import 'css/main.less';
import AppContext from 'flow/contexts/app-context';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import { LocalesData } from 'flow/utils/locales-data';
import { registerInstance } from 'flow/utils/instance-count-utils';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'flow/contexts/query-client';
import { Provider } from 'react-redux';

const root = createRoot(document.getElementById('content'));
import * as microsoftTeams from '@microsoft/teams-js';

const applicationLanguage = getApplicationLanguage();
// hasOwn, not a bare lookup: this code comes from localStorage unvalidated,
// and LocalesData inherits from Object.prototype, so 'constructor' and
// 'toString' would give a truthy object whose fileCode is undefined -- and
// lang="undefined" is the thing the guard below exists to prevent. Leaving it
// undefined lets the markup's own default stand until the locale resolves.
const localeObject = Object.hasOwn(LocalesData, applicationLanguage)
    ? LocalesData[applicationLanguage]
    : undefined;

async function importMomentLocales(LocalesData) {
    // Import all locales for moment
    const locales = Object.values(LocalesData)
        .map((locale) => locale.momentCode)
        .filter(Boolean);

    for (const momentCode of locales) {
        await import(`moment/locale/${momentCode}`);
    }

    moment.locale(localeObject?.momentCode);
}

importMomentLocales(LocalesData);
setLanguageDirection(localeObject);
if (localeObject) {
    addLanguageTohtml(localeObject.fileCode);
}

console.info(appConfig.version);

// INTE-197: Alternate "entrypoint" to complete the authentication flow for Teams integration
if (isTeamsPopupUrl(window.location.pathname)) {
    HomeClient.notAuthorizedHandler = (request) => {
        console.warn('Request was not authorized: ', request);
    };

    root.render(
        <ErrorBoundary>
            <TeamsAuthenticationPopup />
        </ErrorBoundary>,
    );
} else {
    registerInstance();
    electronListeners.registerArgsListener(store.dispatch);

    const lockOrientation =
        screen.lockOrientation ||
        screen.mozLockOrientation ||
        screen.msLockOrientation;

    if (lockOrientation) {
        try {
            lockOrientation('portrait');
        } catch (e) {
            console.warn('Orientation lock failed', e);
        }
    }

    // eslint-disable-next-line consistent-return
    HomeClient.notAuthorizedHandler = async (request) => {
        const { app } = store.getState();
        console.warn('Request was not authorized: ', request);
        const pathName = window.location.pathname;

        // Not advised to do the following, antipattern
        // The user is not logged in, but should get entry to a conference app
        if (pathName.includes('/video') && !isElectron()) {
            store.dispatch(clearState());

            class UnauthorizedVideoConference extends React.Component {
                render() {
                    return (
                        <Provider store={store}>
                            <AppContext.Provider value={{ i18next }}>
                                <Router history={history}>
                                    <MeetingMainUnauthorized />
                                </Router>
                            </AppContext.Provider>
                        </Provider>
                    );
                }
            }

            const renderConferenceApp = () => {
                root.render(
                    <ErrorBoundary>
                        <UnauthorizedVideoConference />
                    </ErrorBoundary>,
                );
            };

            return renderConferenceApp();
        }

        // If the current location renders its own unauthenticated state (login, the
        // UAE Pass callback), we do not want to redirect the user elsewhere or clear
        // state if they supplied wrong credentials.

        const cleanPathName = pathName
            .replace(/^\/[A-Za-z]:/, '') // Remove drive letter with its leading slash
            .replace(/^\/+/, '/'); // Ensure single leading slash

        if (!isUnauthorizedSafePath(cleanPathName)) {
            store.dispatch(disconnectApp(app.get('embedded'))).finally(() => {
                //Clear state from old info when your notAuthorized and/or thrown out
                //This will clear state and unregister your softphone.
                store.dispatch(clearState());
                // clearState keeps loginData; on a 401 / thrown-out we must drop
                // it too so the user is returned to SignIn (isSuccessLogin -> false)
                // instead of staying on Main with a dead session.
                store.dispatch(clearLoginData);
            });
        }
    };

    /**
     * Given a response to a XHR, check whether the user is verified or not.
     *
     * If the header does not exist on the response, we're generous and consider the user to be verified anyway.
     * Otherwise, we might e.g have trouble with the invitation of colleagues to Flow.
     */
    HomeClient.verifyUser = (req) => {
        const receivedExtensionKey = req.getResponseHeader('x-tvx-user');
        const storedExtensionKey = getOwnExtensionKey(store.getState());

        // This is a temporary fix to allow branding requests to go through.
        // API returns a 304 Not Modified which means we can get the data
        // from the cache, but if it is another user than last time
        // the x-tvx-user header will be for the old user which causes us
        // to not verify the request
        if (
            req.responseURL &&
            req.responseURL.includes('api/internal/v2/branding/')
        ) {
            return true;
        }
        return (
            !receivedExtensionKey ||
            !storedExtensionKey ||
            receivedExtensionKey === storedExtensionKey
        );
    };

    const isInTeams = () => {
        return window.location.pathname.includes('embed/teams');
    };

    class Root extends React.PureComponent {
        render() {
            return getRoutes();
        }
    }

    const renderApp = async () => {
        if (isInTeams() && !microsoftTeams.app.isInitialized()) {
            await microsoftTeams.app.initialize();
        }

        root.render(
            <ErrorBoundary>
                <QueryClientProvider client={queryClient}>
                    <Root />
                </QueryClientProvider>
            </ErrorBoundary>,
        );
    };

    i18next.on('loaded', renderApp);

    // if (module.hot) {
    //     module.hot.accept(['./routes'], renderApp);
    // }
}
